import React, { useEffect, useState } from "react";
import { 
    Button, TextField, Grid, Box, Typography, CircularProgress, 
    Paper, IconButton, Divider, Container, Tooltip 
} from "@mui/material";
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { addStuff } from '../../../redux/userRelated/userHandle';
import { underControl } from '../../../redux/userRelated/userSlice';
import { useToast } from '../../../context/ToastContext';
import styled, { keyframes } from 'styled-components';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import SaveIcon from '@mui/icons-material/Save';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const SubjectForm = () => {
    const [subjects, setSubjects] = useState([{ subName: "", subCode: "", sessions: "" }]);

    const dispatch = useDispatch()
    const navigate = useNavigate()
    const params = useParams()

    const userState = useSelector(state => state.user);
    const { status, currentUser, response, error } = userState;

    const sclassName = params.id
    const adminID = currentUser._id
    const address = "Subject"

    const { showToast } = useToast();
    const [loader, setLoader] = useState(false)

    const handleSubjectNameChange = (index) => (event) => {
        const newSubjects = [...subjects];
        newSubjects[index].subName = event.target.value;
        setSubjects(newSubjects);
    };

    const handleSubjectCodeChange = (index) => (event) => {
        const newSubjects = [...subjects];
        newSubjects[index].subCode = event.target.value;
        setSubjects(newSubjects);
    };

    const handleSessionsChange = (index) => (event) => {
        const newSubjects = [...subjects];
        newSubjects[index].sessions = event.target.value || 0;
        setSubjects(newSubjects);
    };

    const handleAddSubject = () => {
        setSubjects([...subjects, { subName: "", subCode: "", sessions: "" }]);
    };

    const handleRemoveSubject = (index) => () => {
        const newSubjects = [...subjects];
        newSubjects.splice(index, 1);
        setSubjects(newSubjects);
    };

    const fields = {
        sclassName,
        subjects: subjects.map((subject) => ({
            subName: subject.subName,
            subCode: subject.subCode,
            sessions: subject.sessions,
        })),
        adminID,
    };

    const submitHandler = (event) => {
        event.preventDefault();
        setLoader(true)
        dispatch(addStuff(fields, address))
    };

    useEffect(() => {
        if (status === 'added') {
            showToast("Subjects added successfully!", "success");
            navigate("/Admin/subjects");
            dispatch(underControl())
            setLoader(false)
        }
        else if (status === 'failed') {
            showToast(response, "error");
            setLoader(false)
        }
        else if (status === 'error') {
            showToast("Network Error", "error");
            setLoader(false)
        }
    }, [status, navigate, error, response, dispatch]);

    return (
        <StyledContainer>
            <StyledPaper elevation={3}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
                    <IconButton onClick={() => navigate(-1)} sx={{ bgcolor: '#f5f5f5' }}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a1a2e' }}>
                            Add Subjects 📖
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            Fill in the details for one or more subjects
                        </Typography>
                    </Box>
                </Box>

                <Divider sx={{ mb: 4 }} />

                <form onSubmit={submitHandler}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        {subjects.map((subject, index) => (
                            <SubjectRow key={index} $isLast={index === subjects.length - 1}>
                                <Grid container spacing={3} alignItems="center">
                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            fullWidth
                                            label="Subject Name"
                                            variant="outlined"
                                            value={subject.subName}
                                            onChange={handleSubjectNameChange(index)}
                                            required
                                            placeholder="e.g. Mathematics"
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <TextField
                                            fullWidth
                                            label="Subject Code"
                                            variant="outlined"
                                            value={subject.subCode}
                                            onChange={handleSubjectCodeChange(index)}
                                            required
                                            placeholder="e.g. MATH101"
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <TextField
                                            fullWidth
                                            label="Sessions"
                                            variant="outlined"
                                            type="number"
                                            inputProps={{ min: 0 }}
                                            value={subject.sessions}
                                            onChange={handleSessionsChange(index)}
                                            required
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={2}>
                                        {subjects.length > 1 && (
                                            <Tooltip title="Remove Subject">
                                                <IconButton 
                                                    onClick={handleRemoveSubject(index)}
                                                    color="error"
                                                    sx={{ bgcolor: '#fff0f0', '&:hover': { bgcolor: '#ffe0e0' } }}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </Grid>
                                </Grid>
                            </SubjectRow>
                        ))}
                    </Box>

                    <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Button
                            variant="outlined"
                            startIcon={<AddCircleOutlineIcon />}
                            onClick={handleAddSubject}
                            sx={{ 
                                borderRadius: '10px', 
                                px: 3, 
                                py: 1.2,
                                fontWeight: 600,
                                textTransform: 'none',
                                borderColor: '#667eea',
                                color: '#667eea',
                                '&:hover': {
                                    borderColor: '#764ba2',
                                    bgcolor: 'rgba(102, 126, 234, 0.04)'
                                }
                            }}
                        >
                            Add Another Subject
                        </Button>

                        <Button 
                            variant="contained" 
                            type="submit" 
                            disabled={loader}
                            startIcon={loader ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                            sx={{ 
                                borderRadius: '10px', 
                                px: 5, 
                                py: 1.2,
                                fontWeight: 600,
                                textTransform: 'none',
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
                                '&:hover': {
                                    boxShadow: '0 6px 20px rgba(102, 126, 234, 0.4)',
                                }
                            }}
                        >
                            {loader ? 'Saving...' : 'Save Subjects'}
                        </Button>
                    </Box>
                </form>
            </StyledPaper>
        </StyledContainer>
    );
}

export default SubjectForm;

const StyledContainer = styled(Container)`
  padding-top: 40px;
  padding-bottom: 40px;
  animation: ${fadeIn} 0.6s ease-out;
`;

const StyledPaper = styled(Paper)`
  padding: 40px;
  border-radius: 20px !important;
  background-color: white !important;
  box-shadow: 0 10px 30px rgba(0,0,0,0.05) !important;
`;

const SubjectRow = styled(Box)`
  padding: 24px;
  background-color: #fcfcfd;
  border: 1px solid #eee;
  border-radius: 16px;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: #667eea;
    background-color: #fff;
    box-shadow: 0 4px 12px rgba(0,0,0,0.03);
  }
`;